import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { translate } from '@/lib/localeMessages';
import { LANGUAGES, languageLocale } from '@/lib/region';

const valid = (value) => LANGUAGES.some((item) => item.code === value);
const read = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const write = (key, value) => { try { localStorage.setItem(key, value); } catch { /* private mode */ } };
const browserLanguage = () => {
 const lang = (navigator.language || 'en').toLowerCase().split('-')[0];
 return lang === 'fil' ? 'tl' : valid(lang) ? lang : 'en';
};
const LocaleContext = createContext({ language: 'en', locale: 'en-US', t: (text) => text, setLanguage: async () => {}, setSchoolLanguageDefault: () => {}, setSchoolCurrencyDefault: () => {}, operatingCurrency: 'usd' });

export const LocaleProvider = ({ children }) => {
 const { user, setUser } = useAuth();
 const location = useLocation();
 const key = `sp-language:${user?.id || 'visitor'}`;
 const [choice, setChoice] = useState(() => ({ key, value: read(key) }));
 const [schoolLanguage, setSchoolLanguage] = useState(null);
 const [schoolCurrency, setSchoolCurrency] = useState('usd');
 const setSchoolCurrencyDefault = useCallback((currency) => {
   const code = /^[a-z]{3}$/i.test(currency || '') ? currency.toLowerCase() : 'usd';
   document.documentElement.dataset.schoolCurrency = code;
   setSchoolCurrency(code);
 }, []);
 const supportedRoute = /^\/(instructor|parent|coach|pay-invoice|enrollment-invite)(\/|$)/.test(location.pathname);
 const saved = user?.interface_language || (user?.role === 'instructor' ? user?.instructor_profile?.interface_language : null);
 const selected = choice.key === key ? choice.value : read(key);
 const language = supportedRoute ? (valid(selected) ? selected : valid(saved) ? saved : valid(schoolLanguage) ? schoolLanguage : browserLanguage()) : 'en';
 const locale = languageLocale(language);
 const t = useCallback((text, params) => translate(text, language, params), [language]);
 useEffect(() => { document.documentElement.lang = language === 'tl' ? 'fil' : language; }, [language]);
 const publicSlug = location.pathname.match(/^\/coach\/([^/]+)/)?.[1] || null;
 useEffect(() => {
   let current = true;
   setSchoolLanguage(null);
   if (publicSlug) api.get(`/public/coaches/${encodeURIComponent(decodeURIComponent(publicSlug))}/locale`).then(({ data }) => { if (current) { setSchoolLanguage(data.interface_language); setSchoolCurrencyDefault(data.operating_currency); } }).catch(() => {});
   return () => { current = false; };
 }, [publicSlug, key, setSchoolCurrencyDefault]);
 const setLanguage = useCallback(async (value, { persist = true } = {}) => {
   if (!valid(value)) return;
   if (persist && user && ['instructor', 'parent'].includes(user.role)) {
     await api.patch('/account/language', { interface_language: value });
     setUser((current) => current ? { ...current, interface_language: value } : current);
   }
   write(key, value);
   setChoice({ key, value });
 }, [key, user, setUser]);
 const setSchoolLanguageDefault = useCallback((value) => setSchoolLanguage(valid(value) ? value : null), []);
 const operatingCurrency = user?.role === 'instructor' ? (user?.instructor_profile?.operating_currency || 'usd') : schoolCurrency;
 const value = useMemo(() => ({ language, locale, t, setLanguage, setSchoolLanguageDefault, setSchoolCurrencyDefault, operatingCurrency }), [language, locale, t, setLanguage, setSchoolLanguageDefault, setSchoolCurrencyDefault, operatingCurrency]);
 return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};
export const useLocale = () => useContext(LocaleContext);
export const T = ({ text }) => { const { t } = useLocale(); return <>{t(text)}</>; };
